import dayjs from 'dayjs';
import pdfMake from "pdfmake/build/pdfmake";
import vfsFonts from "pdfmake/build/vfs_fonts";
import { formatCurrency, numberToThaiText } from '../utils/helpers';
import { loadImageBase64 } from "../utils/loadImageBase64";
import { buildUploadUrl } from '../config/runtimeConfig';
import { getCompanySignatureLayout, normalizeSignatureLayout } from '../utils/signatureLayout';
import { firstNonBlank } from '../utils/contactDisplayName';

// กำหนด font สำหรับ pdfMake
pdfMake.vfs = vfsFonts;
pdfMake.fonts = {
    THSarabunNew: {
        normal: 'THSarabunNew.ttf',
        bold: 'THSarabunNew Bold.ttf',
        italics: 'THSarabunNew Italic.ttf',
        bolditalics: 'THSarabunNew BoldItalic.ttf'
    }
};

// กำหนด font สำหรับ pdfMake
const initializePdfMake = () => {
    if (window.pdfMake) {
        window.pdfMake.fonts = {
            THSarabunNew: {
                normal: 'THSarabunNew.ttf',
                bold: 'THSarabunNew Bold.ttf',
                italics: 'THSarabunNew Italic.ttf',
                bolditalics: 'THSarabunNew BoldItalic.ttf'
            }
        };
    }
};

// ฟังก์ชันสร้าง PDF ใบบันทึกค่าบริการ
export const generateExpensePDF = async (
    expense,
    company = {},
    supplierData = null,
    signatureLayout = null,
    printOptions = {}
) => {

    // โหลด logo และ signature
    const logoBase64 = await loadImageBase64(buildUploadUrl(company?.logo_path));
    const signatureLogo = await loadImageBase64(buildUploadUrl(company?.signature_path));

    // ตรวจว่ามี prefix หรือไม่
    let fixedSignatureLogo = signatureLogo;
    if (signatureLogo && !signatureLogo.startsWith('data:image')) {
        fixedSignatureLogo = `data:image/png;base64,${signatureLogo}`;
    }

    // ตรวจสอบว่า pdfMake โหลดแล้วหรือยัง
    if (!window.pdfMake) {
        throw new Error('pdfMake library is not loaded');
    }

    // กำหนด font
    initializePdfMake();

    const totalAmount = expense.total_amount || 0;
    const taxAmount = expense.tax_amount || 0;
    const amount = expense.amount || 0;
    const subtotal = expense.subtotal || amount;
    const discount = expense.discount || 0;
    const vatMode = expense.vat_mode || 'exclude';
    const taxRate = expense.tax_rate || 7;
    const resolvedSignatureLayout = signatureLayout
        ? normalizeSignatureLayout(signatureLayout, 'expense')
        : getCompanySignatureLayout(company, 'expense');
    const recorderName = String(
        printOptions.recorder_name || expense.created_by_name || company.signature_name || '-'
    ).trim();

    // เตรียมข้อมูลผู้จำหน่าย
    const supplierInfo = supplierData ? {
        name: firstNonBlank(supplierData.name, expense.supplier_name, '-'),
        displayName: firstNonBlank(
            supplierData.company_name,
            expense.supplier_company,
            supplierData.name,
            expense.supplier_name,
            '-'
        ),
        address: supplierData.address || '',
        phone: supplierData.phone || '',
        taxId: supplierData.tax_id || ''
    } : {
        name: firstNonBlank(expense.supplier_name, '-'),
        displayName: firstNonBlank(
            expense.supplier_company,
            expense.supplier_name,
            '-'
        ),
        address: '',
        phone: '',
        taxId: ''
    };

    // สร้าง document definition
    const docDefinition = {
        pageSize: 'A4',
        pageMargins: [40, 20, 40, 60],
        defaultStyle: {
            font: 'THSarabunNew',
            fontSize: 16
        },
        styles: {
            header: {
                fontSize: 20,
                bold: true,
                alignment: 'center',
                margin: [0, 0, 0, 10]
            },
            subheader: {
                fontSize: 16,
                bold: true,
                margin: [0, 10, 0, 5]
            },
            tableHeader: {
                bold: true,
                fontSize: 16,
                color: 'black',
                fillColor: '#eeeeee'
            },
            total: {
                fontSize: 16,
                bold: true,
                alignment: 'right'
            }
        },
        content: [
            // Logo และหัวข้อ
            {
                columns: [
                    {
                        width: 100,
                        stack: [
                            logoBase64 ? {
                                image: logoBase64,
                                width: 60,
                                alignment: 'left'
                            } : { text: '' }
                        ]
                    },
                    {
                        stack: [
                            { text: 'บันทึกค่าใช้จ่าย', style: 'header' },
                            { text: 'EXPENSE RECORD', fontSize: 14, alignment: 'center', color: '#666' }
                        ],
                        width: '*'
                    },
                    {
                        width: 100,
                        text: ''
                    }
                ],
                margin: [0, 0, 0, 5]
            },

            // ข้อมูลบริษัท
            {
                columns: [
                    {
                        width: '50%',
                        stack: [
                            { text: company.company_name || 'ชื่อบริษัท', bold: true, fontSize: 18 },
                            { text: company.address || 'ที่อยู่บริษัท', margin: [0, 1, 0, 0] },
                            { text: `โทร: ${company.phone || '-'}`, margin: [0, 1, 0, 0] },
                            { text: `เลขประจำตัวผู้เสียภาษี: ${company.tax_id || '-'}`, margin: [0, 1, 0, 0] }
                        ]
                    },
                    {
                        width: '50%',
                        stack: [
                            { text: `เลขที่: ${expense.expense_number || '-'}`, alignment: 'right', bold: true, fontSize: 18 },
                            { text: `วันที่: ${dayjs(expense.expense_date).format('DD/MM/YYYY')}`, alignment: 'right', margin: [0, 5, 0, 0] },
                            { text: `หมวดหมู่: ${expense.category || '-'}`, alignment: 'right', margin: [0, 3, 0, 0] },
                            {
                                text: `สถานะ: ${getExpenseStatusText(expense.status)}`,
                                alignment: 'right',
                                margin: [0, 3, 0, 0],
                                color: getExpenseStatusColor(expense.status)
                            }
                        ]
                    }
                ],
                margin: [0, 0, 0, 10]
            },

            // ข้อมูลผู้จำหน่าย/ผู้รับเงิน
            {
                stack: [
                    { text: 'ผู้จำหน่าย/ผู้รับเงิน', style: 'subheader' },
                    { text: supplierInfo.displayName, bold: true },
                    supplierInfo.address ? { text: supplierInfo.address, margin: [0, 1, 0, 0] } : {},
                    supplierInfo.phone ? { text: `โทร: ${supplierInfo.phone}`, margin: [0, 1, 0, 0] } : {},
                    supplierInfo.taxId ? { text: `เลขประจำตัวผู้เสียภาษี: ${supplierInfo.taxId}`, margin: [0, 1, 0, 0] } : {}
                ],
                margin: [0, 0, 0, 10]
            },

            // ข้อมูลโครงการ (ถ้ามี)
            expense.project_name ? {
                stack: [
                    { text: 'โครงการ', style: 'subheader' },
                    { text: `${expense.project_name}${expense.project_code ? ` (${expense.project_code})` : ''}`, bold: true }
                ],
                margin: [0, 0, 0, 10]
            } : {},

            // รายละเอียดค่าใช้จ่าย
            {
                text: 'รายละเอียดค่าใช้จ่าย',
                style: 'subheader'
            },
            {
                text: expense.description || '-',
                margin: [0, 5, 0, 5]
            },

            // ตารางรายการ (ถ้ามี items)
            expense.items && expense.items.length > 0 ? {
                table: {
                    headerRows: 1,
                    widths: ['*', 'auto', 'auto', 'auto'],
                    body: [
                        [
                            { text: 'รายการ', style: 'tableHeader' },
                            { text: 'จำนวน', style: 'tableHeader', alignment: 'center' },
                            { text: 'ราคาต่อหน่วย', style: 'tableHeader', alignment: 'right' },
                            { text: 'รวม', style: 'tableHeader', alignment: 'right' }
                        ],
                        ...(expense.items || []).map(item => [
                            { text: item.description || '-' },
                            { text: (item.quantity || 0).toString(), alignment: 'center' },
                            { text: `${formatCurrency(item.unit_price || 0)} บาท`, alignment: 'right' },
                            { text: `${formatCurrency(item.total_price || (item.quantity * item.unit_price) || 0)} บาท`, alignment: 'right' }
                        ])
                    ]
                },
                layout: {
                    fillColor: function (rowIndex) {
                        return (rowIndex === 0) ? '#eeeeee' : null;
                    }
                },
                margin: [0, 0, 0, 10]
            } : {},

            // สรุปยอดเงิน
            {
                columns: [
                    { width: '60%', text: '' },
                    {
                        width: '40%',
                        table: {
                            body: [
                                [
                                    { text: 'รวมเป็นเงิน:', alignment: 'right', border: [false, false, false, false] },
                                    { text: `${formatCurrency(subtotal)} บาท`, alignment: 'right', border: [false, false, false, false] }
                                ],
                                discount > 0 ? [
                                    { text: 'ส่วนลด:', alignment: 'right', border: [false, false, false, false], color: 'red' },
                                    { text: `-${formatCurrency(discount)} บาท`, alignment: 'right', border: [false, false, false, false], color: 'red' }
                                ] : [],
                                vatMode !== 'no_tax' ? [
                                    { text: `ภาษีมูลค่าเพิ่ม ${taxRate}%:`, alignment: 'right', border: [false, false, false, false] },
                                    { text: `${formatCurrency(taxAmount)} บาท`, alignment: 'right', border: [false, false, false, false] }
                                ] : [],
                                [
                                    { text: 'รวมทั้งสิ้น:', bold: true, alignment: 'right', fontSize: 18, border: [false, true, false, false] },
                                    { text: `${formatCurrency(totalAmount)} บาท`, bold: true, alignment: 'right', fontSize: 18, border: [false, true, false, false] }
                                ],

                            ].filter(row => row.length > 0)
                        },
                        layout: 'noBorders'
                    }
                ],
                margin: [0, 0, 0, 0]
            },

            { text: `(${numberToThaiText(totalAmount)})`, colSpan: 2, alignment: 'right', border: [false, false, false, false], margin: [0, 0, 20, 0] },

            // วิธีการชำระเงิน
            // expense.payment_method ? {
            //     stack: [
            //         { text: 'วิธีการชำระเงิน', style: 'subheader' },
            //         { text: getPaymentMethodText(expense.payment_method) }
            //     ],
            //     margin: [0, 0, 0, 20]
            // } : {},

            // หมายเหตุ
            expense.notes ? {
                stack: [
                    { text: 'หมายเหตุ', style: 'subheader' },
                    { text: expense.notes }
                ],
                margin: [0, 0, 0, 10]
            } : { text: '', margin: [0, 0, 0, 10] },

            // ลายเซ็น
            {
                columns: [
                    {
                        width: '50%',
                        stack: [
                            fixedSignatureLogo ? {
                                image: fixedSignatureLogo,
                                width: resolvedSignatureLayout.width,
                                alignment: 'center',
                                relativePosition: { x: resolvedSignatureLayout.x, y: resolvedSignatureLayout.y },
                                margin: [0, 0, 0, -Math.round(resolvedSignatureLayout.width * 0.44)]
                            } : { text: '\n\n\n', alignment: 'center' },
                            { text: '......................................', alignment: 'center' },
                            { text: `( ${recorderName} )`, alignment: 'center', margin: [0, 5, 0, 0] },
                            { text: 'ผู้บันทึก', alignment: 'center', margin: [0, 3, 0, 0] },
                            // { text: `วันที่ ${dayjs(expense.expense_date).format('DD/MM/YYYY')}`, alignment: 'center', margin: [0, 3, 0, 0] }
                        ]
                    },
                    // {
                    //     width: '50%',
                    //     stack: [
                    //         { text: '\n\n\n' },
                    //         { text: 'ลงชื่อ ......................................', alignment: 'center' },
                    //         { text: `(                                          )`, alignment: 'center', margin: [0, 5, 0, 0] },
                    //         { text: 'ผู้อนุมัติ', alignment: 'center', margin: [0, 3, 0, 0] },
                    //         { text: expense.approved_at ? `วันที่ ${dayjs(expense.approved_at).format('DD/MM/YYYY')}` : '', alignment: 'center', margin: [0, 3, 0, 0] }
                    //     ]
                    // }
                ]
            }
        ],
        footer: function (currentPage, pageCount) {
            return {
                columns: [
                    { text: `หน้า ${currentPage} จาก ${pageCount}`, alignment: 'center', fontSize: 14, margin: [0, 10, 0, 0] }
                ]
            };
        }
    };

    try {
        const pdfDocGenerator = window.pdfMake.createPdf(docDefinition);

        return new Promise((resolve, reject) => {
            pdfDocGenerator.getDataUrl((dataUrl) => {
                if (dataUrl) resolve(dataUrl);
                else reject(new Error('ไม่สามารถสร้าง PDF preview ได้'));
            });
        });

    } catch (error) {
        console.error('Error generating PDF:', error);
        throw new Error('ไม่สามารถสร้าง PDF ได้: ' + error.message);
    }
};

// Helper function to get expense status text in Thai
const getExpenseStatusText = (status) => {
    const statusMap = {
        'pending': 'รอดำเนินการ',
        'approved': 'อนุมัติแล้ว',
        'paid': 'จ่ายแล้ว',
        'rejected': 'ไม่อนุมัติ'
    };
    return statusMap[status] || status;
};

// Helper function to get expense status color
const getExpenseStatusColor = (status) => {
    const colorMap = {
        'pending': '#faad14',
        'approved': '#52c41a',
        'paid': '#1890ff',
        'rejected': '#ff4d4f'
    };
    return colorMap[status] || '#000000';
};

// Helper function to get payment method text in Thai
const getPaymentMethodText = (method) => {
    const methodMap = {
        'cash': 'เงินสด',
        'transfer': 'โอนเงิน',
        'credit_card': 'บัตรเครดิต',
        'debit_card': 'บัตรเดบิต',
        'check': 'เช็ค',
        'other': 'อื่นๆ'
    };
    return methodMap[method] || method;
};
